import { useQuery } from '@tanstack/react-query'
import { Link, Outlet } from 'react-router'
import { IconTile } from '@/components/brand/icon-tile'
import { Logo } from '@/components/brand/logo'
import { SoundBars } from '@/components/brand/scene'
import { ghostActionClass, linkClass } from '@/components/shared/styles'
import { Button } from '@/components/ui/button'
import { adminMeQuery } from '@/lib/api/admin-queries'
import { cn } from '@/lib/utils'

/**
 * Guards the platform console (`/admin`). The server is the authority: every `/api/admin/*`
 * route checks the grant itself on every request. This only avoids painting a console, with its
 * navigation, to someone who would get 403s on every panel.
 *
 * A person with no grant and a console that is switched off see the same calm screen, by
 * design (the API answers 403 and 404, and neither says which). An API that does not answer
 * is kept apart from both: it has to look unreachable, not like "you may not".
 */
export function RequirePlatform() {
  const access = useQuery(adminMeQuery)

  if (access.isPending) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Checking your access"
        className="grid min-h-svh place-items-center bg-background"
      >
        <div className="flex flex-col items-center gap-5">
          <Logo variant="mark" className="size-10" />
          <SoundBars />
        </div>
      </div>
    )
  }

  if (access.isError) {
    return (
      <Screen icon="warn" tone="warm" title="Can’t check your access">
        <p className="mt-1.5 text-small text-muted-foreground">
          The API may be starting up or unreachable.
        </p>
        <Button
          variant="outline"
          className={cn(ghostActionClass, 'mt-6 w-full')}
          onClick={() => void access.refetch()}
          disabled={access.isFetching}
        >
          {access.isFetching ? 'Retrying…' : 'Try again'}
        </Button>
      </Screen>
    )
  }

  if (access.data.kind === 'forbidden') {
    return (
      <Screen icon="lock" tone="cool" title="The platform console isn’t available to you">
        <p className="mt-1.5 text-small text-muted-foreground">
          It’s for Ringout’s own team. Your business’s dashboard is unaffected.
        </p>
        <Link to="/dashboard" className={cn(linkClass, 'mt-6 inline-block text-small')}>
          Back to your dashboard
        </Link>
      </Screen>
    )
  }

  return <Outlet />
}

function Screen({
  icon,
  tone,
  title,
  children,
}: {
  icon: 'warn' | 'lock'
  tone: 'warm' | 'cool'
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="grid min-h-svh place-items-center bg-background p-4">
      <div className="w-full max-w-md rounded-[22px] border border-border bg-card p-6 shadow-card-lift sm:p-8">
        <div role="alert" className="flex items-start gap-4">
          <IconTile icon={icon} tone={tone} size="lg" />
          <div className="min-w-0 pt-0.5">
            <h1 className="text-subtitle font-extrabold">{title}</h1>
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}
